import React, { useState, useMemo, useRef } from "react";
import Page from "../components/Page";
import {
  IconFileSpreadsheet,
  IconPrinter,
  IconSearch,
  IconBuildingStore,
  IconReceiptTax,
  IconReceipt,
  IconCash,
  IconCalendar,
  IconFilter,
  IconDownload
} from "@tabler/icons-react";
import { useKharidKhata } from "../controllers/accounting.controller";
import {
  NEPALI_MONTHS,
  getCurrentBsDate,
  getAvailableFiscalYears
} from "../helpers/NepaliDateHelper";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";

export default function KharidKhataPage() {
  const currentBs = useMemo(() => getCurrentBsDate(), []);
  const fiscalYears = useMemo(() => getAvailableFiscalYears(), []);

  const [selectedFy, setSelectedFy] = useState(currentBs.fiscalYear || "2083/84");
  const [selectedMonth, setSelectedMonth] = useState(currentBs.bsMonth || 1); // 1 = Baisakh, null for full year
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("schedule9"); // 'schedule9' | 'supplierSummary'

  const printRef = useRef();

  const { data, entries, summary, supplierSummary, isLoading } = useKharidKhata({
    fiscalYear: selectedFy,
    nepaliMonth: selectedMonth || null
  });

  // Filtered entries by search query
  const filteredEntries = useMemo(() => {
    if (!entries || !Array.isArray(entries)) return [];
    if (!search.trim()) return entries;
    const q = search.toLowerCase();
    return entries.filter(
      (e) =>
        e.invoice_no?.toLowerCase().includes(q) ||
        e.supplier_name?.toLowerCase().includes(q) ||
        e.supplier_pan?.toLowerCase().includes(q) ||
        e.nepali_date?.includes(q)
    );
  }, [entries, search]);

  // Export to Excel (Schedule 9 format)
  const handleExportExcel = () => {
    try {
      const monthObj = NEPALI_MONTHS.find((m) => m.id === Number(selectedMonth));
      const monthTitle = monthObj ? monthObj.name : "All Months";

      const headers = [
        "S.N.",
        "Miti (B.S.)",
        "Date (A.D.)",
        "Invoice / Bill No",
        "Supplier Name",
        "Supplier PAN",
        "Purchase Type",
        "Taxable Purchase (NPR)",
        "13% VAT (NPR)",
        "Exempt Purchase (NPR)",
        "Total Purchase (NPR)",
        "Paid Amount (NPR)",
        "Due Balance (NPR)"
      ];

      const rows = filteredEntries.map((e, idx) => [
        idx + 1,
        e.nepali_date || "",
        e.invoice_date ? new Date(e.invoice_date).toISOString().substring(0, 10) : "",
        e.invoice_no || "",
        e.supplier_name || "",
        e.supplier_pan || "N/A",
        e.purchase_type || "Goods",
        Number(e.taxable_amount || 0).toFixed(2),
        Number(e.vat_amount || 0).toFixed(2),
        Number(e.exempt_amount || 0).toFixed(2),
        Number(e.total_amount || 0).toFixed(2),
        Number(e.paid_amount || 0).toFixed(2),
        Number(e.due_amount || 0).toFixed(2)
      ]);

      // Add Total Row
      rows.push([
        "TOTAL",
        "",
        "",
        "",
        "",
        "",
        "",
        Number(summary.total_taxable || 0).toFixed(2),
        Number(summary.total_vat || 0).toFixed(2),
        Number(summary.total_exempt || 0).toFixed(2),
        Number(summary.total_purchases || 0).toFixed(2),
        Number(summary.total_paid || 0).toFixed(2),
        Number(summary.total_due || 0).toFixed(2)
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([
        [`KHARID KHATA (PURCHASE BOOK) - SCHEDULE 9 (अनुसूची ९)`],
        [`Fiscal Year: ${selectedFy} | Month: ${monthTitle}`],
        [],
        headers,
        ...rows
      ]);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Kharid Khata");
      XLSX.writeFile(workbook, `Kharid_Khata_${selectedFy.replace("/", "-")}_${monthTitle}.xlsx`);
      toast.success("Kharid Khata exported to Excel successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export Excel file");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Page className="print:p-0 print:bg-white">
      {/* ─── SCREEN-ONLY HEADER ─── */}
      <div className="space-y-6 print:hidden">
        {/* Top Header Card */}
        <div className="bg-gradient-to-r from-emerald-700 via-teal-700 to-cyan-800 rounded-3xl p-6 md:p-8 text-white shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-md text-xs font-black uppercase tracking-wider mb-2">
              <IconReceiptTax size={14} /> IRD Schedule 9 (अनुसूची ९)
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight">Kharid Khata (Purchase Book)</h1>
            <p className="text-emerald-100 text-sm mt-1 max-w-xl">
              Official purchase register for Nepal VAT compliance. Tracks taxable purchases, 13% Input VAT, exempt amounts, and supplier balances in Bikram Sambat.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <button
              onClick={handleExportExcel}
              className="py-3 px-5 bg-white text-emerald-900 hover:bg-emerald-50 rounded-2xl font-black text-xs flex items-center gap-2 transition shadow-lg shadow-black/10 active:scale-95"
            >
              <IconFileSpreadsheet size={18} className="text-emerald-600" /> Export Excel (.xlsx)
            </button>
            <button
              onClick={handlePrint}
              className="py-3 px-5 bg-emerald-600/60 hover:bg-emerald-600 text-white rounded-2xl font-black text-xs flex items-center gap-2 transition border border-white/20 active:scale-95"
            >
              <IconPrinter size={18} /> Print Register
            </button>
          </div>
        </div>

        {/* Filters Bar: Fiscal Year & Months */}
        <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-3xl p-5 shadow-sm space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Fiscal Year Selector */}
            <div className="flex items-center gap-3">
              <span className="text-xs font-black uppercase tracking-wider text-gray-400 flex items-center gap-1.5 shrink-0">
                <IconCalendar size={16} className="text-emerald-500" /> Fiscal Year:
              </span>
              <select
                value={selectedFy}
                onChange={(e) => setSelectedFy(e.target.value)}
                className="select select-sm select-bordered rounded-xl bg-gray-50 dark:bg-gray-800 font-black text-xs outline-none"
              >
                {fiscalYears.map((fy) => (
                  <option key={fy} value={fy}>
                    FY {fy} (B.S.)
                  </option>
                ))}
              </select>
            </div>

            {/* Search Box */}
            <div className="relative flex-1 max-w-md">
              <IconSearch size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search Bill #, Supplier, PAN..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl py-2 pl-10 pr-4 text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-500 transition"
              />
            </div>
          </div>

          {/* Nepali Month Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
            <button
              onClick={() => setSelectedMonth(null)}
              className={`px-3 py-1.5 rounded-xl text-xs font-black shrink-0 transition ${
                selectedMonth === null
                  ? "bg-emerald-600 text-white shadow-md shadow-emerald-500/20"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              Full Year
            </button>
            {NEPALI_MONTHS.map((m) => (
              <button
                key={m.id}
                onClick={() => setSelectedMonth(m.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold shrink-0 transition ${
                  selectedMonth === m.id
                    ? "bg-emerald-600 text-white shadow-md shadow-emerald-500/20 font-black"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
              >
                {m.name} <span className="text-[10px] opacity-70">({m.nepaliName})</span>
              </button>
            ))}
          </div>
        </div>

        {/* KPI Metrics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-wider text-gray-400 block">Total Purchases</span>
            <span className="text-xl font-black text-gray-900 dark:text-white mt-1 block">
              Rs. {Number(summary.total_purchases || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[10px] text-gray-400 mt-1 block">{summary.record_count || 0} Bills</span>
          </div>

          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-wider text-emerald-600 block">Taxable Purchases</span>
            <span className="text-xl font-black text-emerald-600 mt-1 block">
              Rs. {Number(summary.total_taxable || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[10px] text-gray-400 mt-1 block">13% VAT Base</span>
          </div>

          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-wider text-teal-600 block">13% Input VAT</span>
            <span className="text-xl font-black text-teal-600 mt-1 block">
              Rs. {Number(summary.total_vat || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[10px] text-gray-400 mt-1 block">Input Tax Credit</span>
          </div>

          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-wider text-amber-600 block">Exempt / Non-VAT</span>
            <span className="text-xl font-black text-amber-600 mt-1 block">
              Rs. {Number(summary.total_exempt || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[10px] text-gray-400 mt-1 block">Schedule 1 Exempt</span>
          </div>

          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-wider text-cyan-600 block">Total Paid</span>
            <span className="text-xl font-black text-cyan-600 mt-1 block">
              Rs. {Number(summary.total_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[10px] text-gray-400 mt-1 block">Settled to Suppliers</span>
          </div>

          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-wider text-rose-600 block">Accounts Payable</span>
            <span className="text-xl font-black text-rose-600 mt-1 block">
              Rs. {Number(summary.total_due || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[10px] text-rose-400 mt-1 block">Pending Dues</span>
          </div>
        </div>

        {/* View Switcher: Schedule 9 Register vs Supplier Summary */}
        <div className="flex border-b border-gray-200 dark:border-gray-800">
          <button
            onClick={() => setActiveTab("schedule9")}
            className={`py-3 px-5 text-xs font-black transition border-b-2 ${
              activeTab === "schedule9"
                ? "border-emerald-600 text-emerald-600"
                : "border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            Schedule 9 Purchase Register ({filteredEntries.length})
          </button>
          <button
            onClick={() => setActiveTab("supplierSummary")}
            className={`py-3 px-5 text-xs font-black transition border-b-2 ${
              activeTab === "supplierSummary"
                ? "border-emerald-600 text-emerald-600"
                : "border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            Supplier Wise Summary ({supplierSummary?.length || 0})
          </button>
        </div>
      </div>

      {/* ─── PRINT-ONLY STATUTORY HEADER ─── */}
      <div className="hidden print:block mb-6 text-center space-y-1">
        <h2 className="text-xl font-bold uppercase">Kharid Khata (Purchase Book)</h2>
        <h3 className="text-sm font-semibold">अनुसूची ९ (नियम २३ को उपनियम (१) को खण्ड (छ) सँग सम्बन्धित)</h3>
        <p className="text-xs">
          करदाताको नाम: Hotel / Restaurant | आ.व.: {selectedFy} | महिना:{" "}
          {NEPALI_MONTHS.find((m) => m.id === Number(selectedMonth))?.name || "Full Year"}
        </p>
      </div>

      {/* ─── TAB 1: SCHEDULE 9 STATUTORY TABLE ─── */}
      {activeTab === "schedule9" && (
        <div className="bg-white dark:bg-restro-gray rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <table className="table w-full text-left text-xs">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-850/80 border-b border-gray-150 dark:border-gray-800 text-[11px] font-black uppercase text-gray-500 tracking-wider">
                  <th className="py-4 px-3 text-center">क्र.सं.<br/>S.N.</th>
                  <th>मिति (B.S.)<br/>Date (A.D.)</th>
                  <th>बीजक नं.<br/>Bill No.</th>
                  <th>आपूर्तिकर्ताको नाम<br/>Supplier Name</th>
                  <th>पान नं.<br/>PAN No.</th>
                  <th>प्रकार<br/>Type</th>
                  <th className="text-right">करयोग्य खरिद<br/>Taxable (Rs.)</th>
                  <th className="text-right">१३% भ्याट<br/>VAT 13% (Rs.)</th>
                  <th className="text-right">कर छुट खरिद<br/>Exempt (Rs.)</th>
                  <th className="text-right">जम्मा खरिद<br/>Total (Rs.)</th>
                  <th className="text-right">भुक्तान<br/>Paid (Rs.)</th>
                  <th className="text-right">बाँकी<br/>Due (Rs.)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 font-medium">
                {isLoading && (
                  <tr>
                    <td colSpan="12" className="py-12 text-center text-gray-400">
                      <span className="loading loading-spinner loading-md"></span>
                      <p className="mt-2 text-xs">Loading Kharid Khata entries...</p>
                    </td>
                  </tr>
                )}

                {!isLoading && filteredEntries.length === 0 && (
                  <tr>
                    <td colSpan="12" className="py-12 text-center text-gray-400">
                      No purchase records found for the selected period.
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  filteredEntries.map((e, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/40 transition">
                      <td className="py-3 px-3 text-center font-bold text-gray-400">{idx + 1}</td>
                      <td>
                        <div className="font-bold text-gray-900 dark:text-gray-100">{e.nepali_date || "—"}</div>
                        <div className="text-[10px] text-gray-400">
                          {e.invoice_date ? new Date(e.invoice_date).toISOString().substring(0, 10) : ""}
                        </div>
                      </td>
                      <td className="font-mono font-bold text-emerald-700 dark:text-emerald-400">
                        {e.invoice_no || `PUR-${e.purchase_id}`}
                      </td>
                      <td>
                        <div className="font-bold text-gray-900 dark:text-white">{e.supplier_name}</div>
                        {e.supplier_phone && <div className="text-[10px] text-gray-400">{e.supplier_phone}</div>}
                      </td>
                      <td className="font-mono text-gray-600 dark:text-gray-300 font-bold">
                        {e.supplier_pan || <span className="text-gray-300">N/A</span>}
                      </td>
                      <td>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                          {e.purchase_type || "Goods"}
                        </span>
                      </td>
                      <td className="text-right font-bold text-gray-900 dark:text-white">
                        {Number(e.taxable_amount || 0).toFixed(2)}
                      </td>
                      <td className="text-right font-bold text-teal-600 dark:text-teal-400">
                        {Number(e.vat_amount || 0).toFixed(2)}
                      </td>
                      <td className="text-right font-medium text-amber-600 dark:text-amber-400">
                        {Number(e.exempt_amount || 0).toFixed(2)}
                      </td>
                      <td className="text-right font-black text-gray-900 dark:text-white">
                        {Number(e.total_amount || 0).toFixed(2)}
                      </td>
                      <td className="text-right font-medium text-cyan-600 dark:text-cyan-400">
                        {Number(e.paid_amount || 0).toFixed(2)}
                      </td>
                      <td className="text-right font-bold text-rose-600 dark:text-rose-400">
                        {Number(e.due_amount || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
              </tbody>
              {filteredEntries.length > 0 && (
                <tfoot>
                  <tr className="bg-emerald-50/70 dark:bg-emerald-950/40 border-t-2 border-emerald-500 font-black text-xs text-gray-900 dark:text-white">
                    <td colSpan="6" className="py-4 px-4 uppercase tracking-wider text-emerald-800 dark:text-emerald-300">
                      Total (जम्मा)
                    </td>
                    <td className="text-right font-black text-emerald-800 dark:text-emerald-300">
                      {Number(summary.total_taxable || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-black text-teal-700 dark:text-teal-300">
                      {Number(summary.total_vat || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-black text-amber-700 dark:text-amber-300">
                      {Number(summary.total_exempt || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-black text-emerald-900 dark:text-emerald-200 text-sm">
                      {Number(summary.total_purchases || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-black text-cyan-700 dark:text-cyan-300">
                      {Number(summary.total_paid || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-black text-rose-700 dark:text-rose-300">
                      {Number(summary.total_due || 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* ─── TAB 2: SUPPLIER SUMMARY TABLE ─── */}
      {activeTab === "supplierSummary" && (
        <div className="bg-white dark:bg-restro-gray rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <table className="table w-full text-left text-xs">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-850/80 border-b border-gray-150 dark:border-gray-800 text-[11px] font-black uppercase text-gray-500 tracking-wider">
                  <th className="py-4 px-3 text-center">S.N.</th>
                  <th>Supplier Name</th>
                  <th>PAN Number</th>
                  <th className="text-center">Bills Count</th>
                  <th className="text-right">Taxable Purchases (Rs.)</th>
                  <th className="text-right">Input VAT (Rs.)</th>
                  <th className="text-right">Exempt (Rs.)</th>
                  <th className="text-right">Total Purchases (Rs.)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 font-medium">
                {(!supplierSummary || supplierSummary.length === 0) && (
                  <tr>
                    <td colSpan="8" className="py-12 text-center text-gray-400">
                      No supplier summary available.
                    </td>
                  </tr>
                )}
                {supplierSummary?.map((s, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/40 transition">
                    <td className="py-3 px-3 text-center font-bold text-gray-400">{idx + 1}</td>
                    <td className="font-bold text-gray-900 dark:text-white">{s.supplier_name}</td>
                    <td className="font-mono font-bold text-gray-600 dark:text-gray-300">
                      {s.supplier_pan || "N/A"}
                    </td>
                    <td className="text-center font-bold text-gray-600 dark:text-gray-300">
                      {s.bill_count}
                    </td>
                    <td className="text-right font-bold text-gray-900 dark:text-white">
                      {Number(s.taxable_amount || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-bold text-teal-600 dark:text-teal-400">
                      {Number(s.vat_amount || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-medium text-amber-600 dark:text-amber-400">
                      {Number(s.exempt_amount || 0).toFixed(2)}
                    </td>
                    <td className="text-right font-black text-gray-900 dark:text-white">
                      {Number(s.total_amount || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Page>
  );
}
